import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaSearch, FaFileAlt } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .tc-wrap {
    min-height: 100vh;
    background: #f0f2f8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    padding-top: 108px;
  }

  /* ── TOP BANNER ── */
  .tc-banner {
    background: linear-gradient(120deg, #1e1b4b 0%, #3730a3 50%, #4f46e5 100%);
    padding: 36px 48px 80px;
    position: relative;
    overflow: hidden;
  }

  .tc-banner::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 280px; height: 280px;
    border-radius: 50%;
    background: rgba(255,255,255,0.04);
  }

  .tc-banner::after {
    content: '';
    position: absolute;
    bottom: -80px; left: 30%;
    width: 360px; height: 360px;
    border-radius: 50%;
    background: rgba(255,255,255,0.03);
  }

  .tc-banner-title {
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.6px;
  }

  .tc-banner-sub {
    margin-top: 6px;
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    font-weight: 400;
  }

  .tc-stats-row {
    display: flex;
    gap: 16px;
    margin-top: 24px;
  }

  .tc-stat-pill {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 40px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    display: flex;
    align-items: center;
    gap: 7px;
    backdrop-filter: blur(6px);
  }

  .tc-stat-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #818cf8;
  }

  /* ── BODY ── */
  .tc-body {
    max-width: 860px;
    margin: -44px auto 0;
    padding: 0 32px 60px;
    position: relative;
    z-index: 2;
  }

  /* ── ADD CARD ── */
  .tc-add-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(30,27,75,0.10);
    padding: 24px 26px;
    margin-bottom: 24px;
  }

  .tc-add-card-header {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: #a5b4fc;
    margin-bottom: 14px;
  }

  .tc-textarea {
    width: 100%;
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 14px 16px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    resize: vertical;
    min-height: 110px;
    transition: border-color 0.2s, box-shadow 0.2s;
    line-height: 1.6;
  }

  .tc-textarea::placeholder { color: #c7c9e8; }

  .tc-textarea:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .tc-add-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }

  .tc-btn-add {
    background: #4f46e5;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 22px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    box-shadow: 0 3px 12px rgba(79,70,229,0.35);
    transition: all 0.18s;
  }

  .tc-btn-add:hover {
    background: #4338ca;
    box-shadow: 0 5px 18px rgba(79,70,229,0.45);
    transform: translateY(-1px);
  }

  /* ── SEARCH BAR ── */
  .tc-searchbar {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(30,27,75,0.07);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }

  .tc-search-icon { color: #a5b4fc; font-size: 13px; flex-shrink: 0; }

  .tc-search {
    flex: 1;
    border: none;
    outline: none;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: transparent;
  }

  .tc-search::placeholder { color: #c7c9e8; }

  /* ── SECTION LABEL ── */
  .tc-section-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #a5b4fc;
    margin-bottom: 14px;
  }

  /* ── TERM ITEMS ── */
  .tc-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .tc-item {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(30,27,75,0.07);
    border: 1.5px solid transparent;
    transition: box-shadow 0.2s, border-color 0.2s;
    animation: itemIn 0.3s ease both;
    overflow: hidden;
  }

  @keyframes itemIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .tc-item:hover {
    box-shadow: 0 6px 22px rgba(79,70,229,0.11);
    border-color: #e0e1ff;
  }

  .tc-item.editing {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }

  .tc-item-body {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 18px 20px 14px;
  }

  .tc-item-num {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, #eef2ff, #e0e7ff);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 800;
    color: #4f46e5;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .tc-item-text {
    flex: 1;
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
    font-weight: 400;
    word-break: break-word;
  }

  .tc-item-edit-textarea {
    flex: 1;
    border: 1.5px solid #6366f1;
    border-radius: 8px;
    padding: 10px 13px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    color: #1e1b4b;
    outline: none;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
    resize: vertical;
    min-height: 90px;
    line-height: 1.6;
  }

  .tc-item-footer {
    display: flex;
    gap: 8px;
    padding: 0 20px 16px;
    justify-content: flex-end;
  }

  .tc-item-btn {
    border: none;
    border-radius: 8px;
    padding: 7px 16px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    transition: all 0.15s;
  }

  .tc-item-btn.edit-btn    { background: #f0f1ff; color: #4f46e5; }
  .tc-item-btn.edit-btn:hover    { background: #e0e3ff; }
  .tc-item-btn.del-btn     { background: #fff1f1; color: #ef4444; }
  .tc-item-btn.del-btn:hover     { background: #ffe0e0; }
  .tc-item-btn.save-btn    { background: #4f46e5; color: #fff; box-shadow: 0 2px 8px rgba(79,70,229,0.3); }
  .tc-item-btn.save-btn:hover    { background: #4338ca; }
  .tc-item-btn.cancel-btn  { background: #f3f4f6; color: #6b7280; }
  .tc-item-btn.cancel-btn:hover  { background: #e5e7eb; }

  /* ── EMPTY ── */
  .tc-empty {
    text-align: center;
    padding: 72px 0;
  }

  .tc-empty-emoji { font-size: 48px; margin-bottom: 14px; }
  .tc-empty-txt   { font-size: 15px; font-weight: 600; color: #818cf8; }
  .tc-empty-sub   { font-size: 13px; color: #c7c9e8; margin-top: 4px; }

  @media (max-width: 640px) {
    .tc-banner { padding: 28px 20px 70px; }
    .tc-body   { padding: 0 14px 40px; }
    .tc-banner-title { font-size: 22px; }
  }
`;

export default function TermsConditions() {
  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchTerms = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/terms/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTerms(data);
    } catch { toast.error("Failed to load terms"); }
  };

  useEffect(() => { fetchTerms(); }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("Login required"); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized"); navigate("/"); }
  }, [navigate]);

  const handleAdd = async () => {
    if (!newTerm.trim()) { toast.warning("Enter term text"); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/terms/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: newTerm }),
      });
      if (!res.ok) throw new Error();
      toast.success("Term added");
      setNewTerm("");
      fetchTerms();
    } catch { toast.error("Failed to add term"); }
  };

  const handleUpdate = async (id) => {
    if (!editText.trim()) { toast.warning("Enter valid text"); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/terms/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: editText }),
      });
      if (!res.ok) throw new Error();
      toast.success("Term updated");
      setEditingId(null);
      fetchTerms();
    } catch { toast.error("Update failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this term?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/terms/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.info("Term deleted");
      fetchTerms();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = terms.filter(t =>
    t.text.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{S}</style>
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <AdminNavbar />
      </div>

      <div className="tc-wrap">
        {/* Banner */}
        <div className="tc-banner">
          <div className="tc-banner-title">Terms & Conditions</div>
          <div className="tc-banner-sub">Manage all terms and conditions clauses from one place</div>
          <div className="tc-stats-row">
            <div className="tc-stat-pill">
              <span className="tc-stat-dot" />
              {terms.length} Terms
            </div>
            {search && (
              <div className="tc-stat-pill">
                <span className="tc-stat-dot" style={{ background: "#fbbf24" }} />
                {filtered.length} Results
              </div>
            )}
          </div>
        </div>

        <div className="tc-body">

          {/* Add Term Card */}
          <div className="tc-add-card">
            <div className="tc-add-card-header">
              <FaFileAlt style={{ marginRight: 7, opacity: 0.7 }} />
              Add New Term
            </div>
            <textarea
              className="tc-textarea"
              placeholder="Type the term or condition clause here…"
              value={newTerm}
              onChange={e => setNewTerm(e.target.value)}
            />
            <div className="tc-add-footer">
              <button className="tc-btn-add" onClick={handleAdd}>
                <FaPlus size={11} /> Add Term
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="tc-searchbar">
            <FaSearch className="tc-search-icon" />
            <input
              className="tc-search"
              placeholder="Search terms…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* List */}
          <div className="tc-section-label">All Terms</div>

          {filtered.length === 0 ? (
            <div className="tc-empty">
              <div className="tc-empty-emoji">📄</div>
              <div className="tc-empty-txt">No terms found</div>
              <div className="tc-empty-sub">Try a different search or add one above</div>
            </div>
          ) : (
            <div className="tc-list">
              {filtered.map((term, index) => (
                <div key={term.id} className={`tc-item${editingId === term.id ? " editing" : ""}`}>
                  <div className="tc-item-body">
                    <div className="tc-item-num">{index + 1}</div>
                    {editingId === term.id ? (
                      <textarea
                        className="tc-item-edit-textarea"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        autoFocus
                        onKeyDown={e => { if (e.key === "Escape") setEditingId(null); }}
                      />
                    ) : (
                      <div className="tc-item-text">{term.text}</div>
                    )}
                  </div>
                  <div className="tc-item-footer">
                    {editingId === term.id ? (
                      <>
                        <button className="tc-item-btn save-btn" onClick={() => handleUpdate(term.id)}>
                          <FaSave size={10} /> Save
                        </button>
                        <button className="tc-item-btn cancel-btn" onClick={() => setEditingId(null)}>
                          <FaTimes size={10} /> Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="tc-item-btn edit-btn"
                          onClick={() => { setEditingId(term.id); setEditText(term.text); }}>
                          <FaEdit size={10} /> Edit
                        </button>
                        <button className="tc-item-btn del-btn" onClick={() => handleDelete(term.id)}>
                          <FaTrash size={10} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}